import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, NgModule, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { WINDOW } from '@app/common/app-tokens';
import { BreakPoints } from '@app/common/breakpoints';
import { Utilities } from '@app/common/utilities';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { FeedItem } from '@app/models/feed-item';
import { FeedInfo } from '@app/models/feed-source-info';
import { LinkModule } from '@app/directives/link.directive';
import { FeedService } from '@app/services/feed.service';
import { Observable, of, Subject } from 'rxjs';
import { concatMap, startWith, tap } from 'rxjs/operators';
import { RssFeedAmount } from '@app/common/rss-feed';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { UserLanguageTermService } from '@app/services/user-language-term.service';

@Component({
  selector: 'app-compact-rss-feeds',
  templateUrl: './compact-rss-feeds.component.html',
  styleUrls: ['./compact-rss-feeds.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompactRssFeedsComponent implements OnInit {

  @Input() maxNumberOfShowMore = 2; // How may times can you press the show more button
  @Input() titleMaxLength = 100;
  amountToFetch: number;
  amountToShowPerLoad: number;
  amountCurrentlyShowing: number;
  feedLoading: boolean;
  fetchedAmountOfFeeds: number;
  feedsFrom: Array<FeedInfo>;
  tagName: string;
  isFeedUrlConfigured: boolean;
  subscribedToRssFeature: boolean;
  feeds$: Observable<FeedItem[]>;
  private moreFeedsSubject = new Subject<number>();
  rssFeedConfig = this.featureSettingsStoreService.state.rssFeedConfig;
  startIndex = 0;
  numberOfShowMoreClicks = 0;
  showNext = false;
  onLoad = true;
  feedsToShow: FeedItem[];
  sliced = false;
  @Output() hideRssFeed = new EventEmitter<any>();
  maxNumberOfShowMoreClicks: number;

  constructor(private featureSettingsStoreService: FeatureSettingsStoreService,
              @Inject(WINDOW) private window: Window,
              private service: FeedService,
              private userLanguageTermService: UserLanguageTermService,
              @Inject(DOCUMENT) private document: Document ) { }

  ngOnInit() {
    this.onLoad = true;
    if (this.rssFeedConfig) {

      // Vary amount of items to show based on screen size (and thus breakpoint).
      // Goal: 1) display even amount, and 2) show suitable amount for the resolution
      const smallModes = { [BreakPoints.xs]: 2, [BreakPoints.sm]: 2, [BreakPoints.md]: 4, [BreakPoints.lg]: 3, [BreakPoints.xl]: 3, [BreakPoints.xxl]: 4, [BreakPoints.xxxl]: 4 };
      const mediumModes = { [BreakPoints.xs]: 4, [BreakPoints.sm]: 4, [BreakPoints.md]: 8, [BreakPoints.lg]: 6, [BreakPoints.xl]: 6, [BreakPoints.xxl]: 8, [BreakPoints.xxxl]: 8 };
      const breakpoint = Utilities.getCurrentBootstrapBreakpoint(this.window);
      if (this.rssFeedConfig.amountToShowPerLoad === RssFeedAmount.Small) {
        this.amountToShowPerLoad = smallModes[breakpoint];
      } else { // Medium
        this.amountToShowPerLoad = mediumModes[breakpoint];
      }
      this.amountToShowPerLoad = this.featureSettingsStoreService.state.enableCompactDesign ? 4 : this.amountToShowPerLoad;
      this.amountToFetch = this.amountToShowPerLoad * (this.maxNumberOfShowMore + 1);

      this.subscribedToRssFeature = true;
      this.feedsFrom = this.rssFeedConfig.feedLinksAndTagName;
      this.amountCurrentlyShowing = this.amountToShowPerLoad;

      if (this.feedsFrom.length > 0) {
        this.hideRssFeed.emit(false);
        this.isFeedUrlConfigured = true;
        this.feeds$ = this.moreFeedsSubject
          .pipe(startWith(this.amountCurrentlyShowing))
          .pipe(concatMap((amount) => {
            this.amountCurrentlyShowing = amount;
            this.feedLoading = true;
            // filter feed links based on user interests to rss feed language setting in admin center
            return this.userLanguageTermService.getUserLanguageTerms()
              .pipe(
                concatMap((userChannelTerms) => {
                  const feedinfo = this.feedsFrom.filter(feeds => userChannelTerms.some(channelTerm => (channelTerm.LanguageTermId === feeds.Language)) || !feeds.Language || feeds.Language === '');
                  if (feedinfo.length > 0) {
                    return this.service.getRssFeeds(feedinfo, this.amountToFetch);
                  } else {
                    return of([]);
                  }
                })
              );
          }))
          .pipe(tap((feeds) => {
            if (feeds.length === 0) {
              this.hideRssFeed.emit(true);
            } else {
              this.hideRssFeed.emit(false);
              this.fetchedAmountOfFeeds = feeds.length;
              this.maxNumberOfShowMoreClicks = Math.floor((feeds.length - 1) / this.amountToShowPerLoad);
              if (this.onLoad) {
                this.feedsToShow = feeds.slice(0, this.amountToShowPerLoad);
              } else {
                if (this.showNext) {
                  this.feedsToShow = feeds.slice(this.startIndex, this.startIndex + this.amountToShowPerLoad);
                } else {

                  if (!this.sliced) {
                    this.sliced = true;
                    this.feedsToShow = feeds.slice(this.startIndex - this.amountToShowPerLoad, this.startIndex);
                    if (this.feedsToShow.length === 0) {
                      this.feedsToShow = feeds.slice(this.startIndex, this.startIndex + this.amountToShowPerLoad);
                    }
                  }
                }
                this.amountCurrentlyShowing = this.amountToShowPerLoad;
              }
              this.feedLoading = false;
            }
          }));
      } else {
        this.isFeedUrlConfigured = false;
        this.hideRssFeed.emit(true);
      }
    } else {
     this.hideRssFeed.emit(true);
     this.subscribedToRssFeature = false;
    }
  }

  clickShowNext() {
    this.numberOfShowMoreClicks++;
    this.startIndex =  this.amountCurrentlyShowing * this.numberOfShowMoreClicks;
    this.showNext = true;
    this.onLoad = false;

    this.amountCurrentlyShowing = this.amountCurrentlyShowing + this.amountToShowPerLoad;
    this.moreFeedsSubject.next(this.amountCurrentlyShowing);
  }

  clickShowPrevious() {
    this.amountCurrentlyShowing = this.amountToShowPerLoad;
    this.showNext = false;
    this.onLoad = false;
    this.sliced = false;
    this.moreFeedsSubject.next(this.amountCurrentlyShowing);
    this.numberOfShowMoreClicks -= 1;
    this.startIndex -= this.amountToShowPerLoad;

    console.log('numberOfShowMoreClicks' + this.numberOfShowMoreClicks);
    console.log('startIndex' + this.startIndex);
  }
}
@NgModule({
  declarations: [CompactRssFeedsComponent],
  exports: [CompactRssFeedsComponent],
  imports: [
    CommonModule,
    MatButtonModule,
    TextPlaceholderModule,
    WidgetPlaceholderModule,
    LinkModule,
  ]
})
export class CompactRssFeedsModule { }
